import { useTranslation } from 'react-i18next';
import PageSeo from '@/components/base/PageSeo';
import Breadcrumb from '@/components/base/Breadcrumb';
import { siteInfo, faqs } from '@/mocks/comics';

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <i
          key={i}
          className={`text-sm ${i < rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
        ></i>
      ))}
    </div>
  );
}

export default function AboutPage() {
  const { t } = useTranslation();

  return (
    <>
      <PageSeo
        title="关于我们_韩漫网_韩漫漫画大全"
        description="韩漫网品牌介绍、用户口碑与联系方式。韩漫网致力于为漫画爱好者提供最新、最全、最优质的韩漫资源，了解我们的故事和团队。"
        keywords="韩漫网,关于韩漫网,韩漫漫画大全,韩漫品牌,韩漫联系方式"
        canonicalUrl="https://hanmanwang.com/about"
        ogTitle="关于我们 - 韩漫网"
        ogDescription="韩漫网品牌介绍、用户口碑与联系方式。了解韩漫网的故事、平台特色与用户评价。"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "name": "关于韩漫网",
            "url": "https://hanmanwang.com/about",
            "description": "韩漫网品牌介绍、用户口碑与联系方式。",
            "isPartOf": { "@id": "https://hanmanwang.com/#website" }
          },
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "韩漫网",
            "url": "https://hanmanwang.com/",
            "description": "韩漫网是致力于为漫画爱好者提供最新、最全、最优质的韩漫资源平台。",
            "email": "contact@hanmanwang.com",
            "foundingDate": "2020"
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "首页",
                "item": "https://hanmanwang.com/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "关于我们",
                "item": "https://hanmanwang.com/about"
              }
            ]
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map((faq) => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
              }
            }))
          }
        ]}
      />

      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-6">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: t('about.title') },
          ]}
        />

        <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mt-2">{t('about.title')}</h1>

        <section className="mt-8">
          <h2 className="text-xl font-heading font-bold text-foreground-900 mb-4">{t('about.brandIntro')}</h2>
          <div className="bg-background-50 rounded-xl border border-background-200/70 p-6 md:p-8">
            <p className="text-sm text-foreground-600 leading-relaxed">
              {t('about.brandDesc')}
            </p>
            <p className="text-sm text-foreground-600 leading-relaxed mt-4">
              自{siteInfo.founded}成立以来，韩漫网始终坚持"为读者提供最佳漫画阅读体验"的核心理念，不断扩展漫画资源库。目前平台已收录超过
              <strong className="text-primary-500">{siteInfo.stats.totalComics.toLocaleString()}</strong>
              部精选韩漫作品，累计
              <strong className="text-primary-500">{siteInfo.stats.totalChapters.toLocaleString()}</strong>
              余话章节，服务超过
              <strong className="text-primary-500">{(siteInfo.stats.totalUsers / 10000).toFixed(0)}万</strong>
              位漫画爱好者。我们与多家韩国顶级漫画平台保持着紧密的合作关系，确保第一时间为中文读者带来最新、最热的韩漫内容。
            </p>
          </div>
        </section>

        <section className="mt-10">
          <h2 className="text-xl font-heading font-bold text-foreground-900 mb-4">{t('about.features')}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {siteInfo.features.map((feature, index) => (
              <div
                key={index}
                className="bg-background-50 rounded-xl border border-background-200/70 p-6 hover:border-primary-200 transition-all duration-300"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 mb-4">
                  <i className={`${feature.icon} text-lg`}></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-10">
          <h2 className="text-xl font-heading font-bold text-foreground-900 mb-4">{t('about.reputation')}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {siteInfo.reviews.map((review, index) => (
              <div
                key={index}
                className="bg-background-50 rounded-xl border border-background-200/70 p-6"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 font-bold text-sm">
                      {review.name.charAt(0)}
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900">{review.name}</h4>
                      <p className="text-xs text-foreground-500">{review.date}</p>
                    </div>
                  </div>
                  <StarRating rating={review.rating} />
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed">{review.content}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-10">
          <h2 className="text-xl font-heading font-bold text-foreground-900 mb-4">{t('about.contactTitle')}</h2>
          <div className="bg-background-50 rounded-xl border border-background-200/70 p-6 md:p-8">
            <p className="text-sm text-foreground-600 leading-relaxed mb-6">{t('about.contactDesc')}</p>
            <div className="flex flex-col sm:flex-row gap-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <div>
                  <p className="text-xs text-foreground-500">{t('about.email')}</p>
                  <p className="text-sm font-medium text-foreground-900">{siteInfo.email}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mt-10 mb-10">
          <h2 className="text-xl font-heading font-bold text-foreground-900 mb-4">{t('about.faqTitle')}</h2>
          <div className="flex flex-col gap-3">
            {faqs.map((faq, index) => (
              <details
                key={index}
                className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden group"
              >
                <summary className="flex items-center justify-between cursor-pointer p-5 text-sm font-semibold text-foreground-900 hover:text-primary-500 transition-colors">
                  {faq.question}
                  <i className="ri-arrow-down-s-line text-lg transition-transform duration-300 group-open:rotate-180"></i>
                </summary>
                <div className="px-5 pb-5">
                  <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </section>
      </div>
    </>
  );
}